import React, { memo, useCallback, useMemo } from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import LinkButton from '@/se/components/LinkButton';

/** Props for the PaginationControls component */
export interface PaginationControlsProps {
  /** Current page index (0-based) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total count of items across all pages */
  totalCount: number;
  /** Number of items per page */
  pageSize: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Whether data is currently loading */
  loading?: boolean;
}

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  pageButton: {
    minWidth: '2em',
    height: '2em',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
  },
  activePageButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.primary.contrastText,
    },
  },
}));

/**
 * Pagination controls for server-side paginated lists.
 * Displays item count, page info, and navigation buttons.
 */
const PaginationControls: React.FC<PaginationControlsProps> = memo(
  ({ currentPage, totalPages, totalCount, pageSize, onPageChange, loading = false }) => {
    const classes = useStyles();

    // Derived state
    const start = useMemo(() => Math.min(currentPage * pageSize + 1, totalCount), [currentPage, pageSize, totalCount]);
    const end = useMemo(() => Math.min((currentPage + 1) * pageSize, totalCount), [currentPage, pageSize, totalCount]);
    const canGoPrev = currentPage > 0;
    const canGoNext = currentPage < totalPages - 1;

    // Navigation handlers
    const goToPrev = useCallback(() => onPageChange(currentPage - 1), [onPageChange, currentPage]);
    const goToNext = useCallback(() => onPageChange(currentPage + 1), [onPageChange, currentPage]);
    const goToPage = useCallback((page: number) => onPageChange(page), [onPageChange]);

    // Generate page numbers to display (show range around current page)
    const pageNumbers = useMemo(() => {
      const HRANGE = 3;
      const pages: number[] = [];
      const from = Math.max(0, currentPage - HRANGE);
      const to = Math.min(totalPages, currentPage + HRANGE + 1);
      const extendedFrom = Math.max(0, from - (HRANGE - (to - currentPage - 1)));
      const actualFrom = to - from < 2 * HRANGE ? extendedFrom : from;

      for (let i = Math.max(0, actualFrom); i < to; i++) {
        pages.push(i);
      }
      return pages;
    }, [currentPage, totalPages]);

    // Don't render if no items and not loading
    if (totalCount === 0 && !loading) return null;

    // Don't render pagination if only one page
    if (totalPages <= 1 && !loading) return null;

    // Status text
    const statusText =
      loading && totalCount === 0 ? 'Loading...' : `Showing from ${start} to ${end} out of ${totalCount} entries`;

    return (
      <Box className={classes.container}>
        <Typography variant="body2" color="textSecondary">
          {statusText}
        </Typography>

        <Box className={classes.navigation}>
          <LinkButton onClick={goToPrev} disabled={!canGoPrev || loading}>
            Prev
          </LinkButton>
          {pageNumbers.map(page => (
            <LinkButton
              key={page}
              onClick={() => goToPage(page)}
              disabled={loading}
              className={`${classes.pageButton} ${page === currentPage ? classes.activePageButton : ''}`}
            >
              {page + 1}
            </LinkButton>
          ))}
          <LinkButton onClick={goToNext} disabled={!canGoNext || loading}>
            Next
          </LinkButton>
        </Box>
      </Box>
    );
  }
);

PaginationControls.displayName = 'PaginationControls';

export default PaginationControls;
